import { useCallback, useEffect, useState } from 'react';

const isSupported = () => 'Notification' in window
	&& typeof window.Notification.requestPermission === 'function';

const readPermission = () => isSupported() ? window.Notification.permission : 'unsupported';

// Safari < 16 only implements the callback form of requestPermission()
const askPermission = () => new Promise((resolve) => {
	const result = window.Notification.requestPermission(resolve);

	if( result?.then ) {
		result.then(resolve);
	}
});

/**
 * Desktop (Web Notifications API) permission state and helpers.
 *
 * The permission is never requested on load — browsers penalize sites that ask
 * without context, and Chrome silently auto-denies the prompt for users who
 * have dismissed it a few times. Call `request()` from a user gesture instead,
 * as the notifications panel prompt does.
 */
export default function useDesktopNotifications() {
	const [permission, setPermission] = useState(readPermission);
	const [isRequesting, setIsRequesting] = useState(false);

	// The permission can also change from the browser's own site settings while
	// the tab is open, so keep our copy in step with it.
	useEffect(() => {
		if( !isSupported() ) {
			return;
		}

		let status;
		let isCancelled = false;
		const handleChange = () => setPermission(readPermission());

		// The Permissions API `change` event is the precise signal, but Safari
		// doesn't fire it reliably and not every browser exposes the descriptor
		// at all. Re-reading when the tab regains focus covers those cases — that
		// is exactly when someone coming back from the browser's own settings
		// lands on us.
		window.addEventListener('focus', handleChange);
		document.addEventListener('visibilitychange', handleChange);

		const watch = async() => {
			if( typeof navigator.permissions?.query !== 'function' ) {
				return;
			}

			try {
				const result = await navigator.permissions.query({ name: 'notifications' });

				// the query can settle after the effect has already been cleaned up
				if( isCancelled ) {
					return;
				}

				status = result;
				status.addEventListener('change', handleChange);
			}
			catch{
				// not every browser exposes the notifications descriptor
			}
		};
		watch();

		return () => {
			isCancelled = true;
			window.removeEventListener('focus', handleChange);
			document.removeEventListener('visibilitychange', handleChange);
			status?.removeEventListener('change', handleChange);
		};
	}, []);

	const request = useCallback(async() => {
		if( !isSupported() || window.Notification.permission !== 'default' ) {
			return readPermission();
		}

		setIsRequesting(true);

		try {
			await askPermission();
		}
		finally {
			setIsRequesting(false);
			setPermission(readPermission());
		}

		return readPermission();
	}, []);

	// Reads the permission at call time so the callback stays stable for effects
	const notify = useCallback((title, options) => {
		if( !isSupported() || window.Notification.permission !== 'granted' ) {
			return null;
		}

		try {
			return new window.Notification(title, options);
		}
		catch{
			// some browsers (mobile Chrome) only allow notifications from a service worker
			return null;
		}
	}, []);

	return {
		permission,
		isSupported: isSupported(),
		isRequesting,
		request,
		notify
	};
}
